/**
 * OneFleet console client (OneFleet AD-09, WP-U0). The `/fleet` module calls OneFleet's API same-origin under
 * `/fleet-api`: OneSphere's Traefik strips the prefix and forwards to `OneFleet-api` (`platform/traefik/dynamic/
 * onefleet.yml`); the Vite dev server proxies the same prefix to `localhost:52080`. The realtime service and the
 * AI API follow the same pattern (`/fleet-rt`, `/fleet-ai`).
 *
 * Every request carries the OneSphere access token (the same Keycloak token Frappe validates — OneFleet checks
 * the `onefleet-api` audience), a request id for the audit trail, and the tenant as `X-Company` when the caller
 * names one. The typed operations are generated into `./types.ts` from OneFleet's committed `/int/v1` OpenAPI
 * snapshot (TS-07, WP-U3); until that snapshot exists the system endpoints are typed by hand there.
 */
import { getAccessToken } from "../frappe";

export const FLEET_API = "/fleet-api";
export const FLEET_RT = "/fleet-rt";
export const FLEET_AI = "/fleet-ai";

export class OneFleetError extends Error {
  readonly status: number;
  readonly body: string;
  constructor(status: number, body: string) {
    super(`OneFleet ${status}: ${body.slice(0, 300)}`);
    this.name = "OneFleetError";
    this.status = status;
    this.body = body;
  }
}

export type FleetRequest = RequestInit & {
  /** Tenant (OneFleet company public id) sent as `X-Company`. */
  company?: string;
  /** Query string values; `undefined` values are omitted. */
  query?: Record<string, string | number | boolean | undefined>;
};

const requestId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Headers for a console call: bearer token when signed in, request id, tenant, JSON. */
export function fleetHeaders(company?: string, extra?: HeadersInit): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json", "X-Request-Id": requestId() };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (company) headers["X-Company"] = company;
  for (const [k, v] of Object.entries(extra ?? {})) headers[k] = v as string;
  return headers;
}

export function fleetUrl(path: string, query?: FleetRequest["query"]): string {
  const url = `${FLEET_API}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined) q.set(k, String(v));
  const s = q.toString();
  return s ? `${url}?${s}` : url;
}

export async function fleetFetch<T>(path: string, init: FleetRequest = {}): Promise<T> {
  const { company, query, headers, ...rest } = init;
  const res = await fetch(fleetUrl(path, query), { ...rest, headers: fleetHeaders(company, headers) });
  if (!res.ok) throw new OneFleetError(res.status, await res.text());
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const onefleet = {
  get<T>(path: string, init: FleetRequest = {}): Promise<T> {
    return fleetFetch<T>(path, { ...init, method: "GET" });
  },
  post<T>(path: string, body: unknown, init: FleetRequest = {}): Promise<T> {
    return fleetFetch<T>(path, { ...init, method: "POST", body: JSON.stringify(body), headers: { "Content-Type": "application/json", ...(init.headers as Record<string, string> ?? {}) } });
  },
  patch<T>(path: string, body: unknown, init: FleetRequest = {}): Promise<T> {
    return fleetFetch<T>(path, { ...init, method: "PATCH", body: JSON.stringify(body), headers: { "Content-Type": "application/json", ...(init.headers as Record<string, string> ?? {}) } });
  },
  put<T>(path: string, body: unknown, init: FleetRequest = {}): Promise<T> {
    return fleetFetch<T>(path, { ...init, method: "PUT", body: JSON.stringify(body), headers: { "Content-Type": "application/json", ...(init.headers as Record<string, string> ?? {}) } });
  },
  /** A soft delete on the API (204). */
  delete(path: string, init: FleetRequest = {}): Promise<void> {
    return fleetFetch<void>(path, { ...init, method: "DELETE" });
  },
};
